import { api } from "./api";

interface StoryBrainItem {
  summaryId: string;
}

interface StoryBrainData {
  files?: StoryBrainItem[];
  links?: StoryBrainItem[];
}

const BASE_PATH = "/story-brain";
const createPath = (path: string) => `${BASE_PATH}${path}`;

const handleCollectionUpdate = <T extends StoryBrainItem>(
  draft: StoryBrainData,
  item: T,
  action: "add" | "remove",
  collection: keyof StoryBrainData,
) => {
  const items = draft[collection] ?? [];
  if (action === "add") {
    draft[collection] = [...items, item];
  } else {
    draft[collection] = items.filter(
      (i: StoryBrainItem) => i.summaryId !== item.summaryId,
    );
  }
};

export const storyBrainApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getStoryBrain: builder.query({
      query: ({ id }: { id: string }) => ({
        url: createPath(`/${id}`),
        method: "GET",
      }),
    }),

    initializeStoryBrain: builder.mutation({
      query: ({
        id,
        payload,
      }: {
        id: string;
        payload: Record<string, unknown>;
      }) => ({
        url: createPath(`/${id}/initialize`),
        method: "POST",
        body: { payload },
      }),
      onQueryStarted: async ({ id }, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            storyBrainApi.util.updateQueryData(
              "getStoryBrain",
              { id },
              (draft) => Object.assign({}, draft || {}, data),
            ),
          );
        } catch (error) {
          console.error("Failed to update story brain:", error);
        }
      },
    }),
    updateStoryBrain: builder.mutation({
      query: ({
        id,
        payload,
      }: {
        id: string;
        payload: Record<string, unknown>;
      }) => ({
        url: createPath(`/${id}`),
        method: "POST",
        body: payload,
      }),
      onQueryStarted: async ({ id }, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            storyBrainApi.util.updateQueryData(
              "getStoryBrain",
              { id },
              (draft) => Object.assign({}, draft, data),
            ),
          );
        } catch (error) {
          console.error("Failed to update story brain:", error);
        }
      },
    }),

    summarizeUploadedFile: builder.mutation({
      query: ({
        plainTextId,
        originalFileId,
        storyBrainId,
        fileMetadata,
        signal,
      }: {
        plainTextId: string;
        originalFileId: string;
        storyBrainId: string;
        fileMetadata: {
          title: string;
          type: string;
        };
        signal?: AbortSignal;
      }) => ({
        url: createPath("/summarize-uploaded-file"),
        method: "POST",
        body: { plainTextId, originalFileId, storyBrainId, fileMetadata },
        signal,
      }),
      async onQueryStarted(args, { queryFulfilled, dispatch }) {
        try {
          const {
            data: { file, action },
          } = await queryFulfilled;
          dispatch(
            storyBrainApi.util.updateQueryData(
              "getStoryBrain",
              { id: args.storyBrainId },
              (draft) => {
                handleCollectionUpdate(draft, file, action, "files");
              },
            ),
          );
        } catch (error) {
          console.error("Failed to summarize file:", error);
        }
      },
    }),

    summarizeWebsite: builder.mutation({
      query: ({ url, id }: { url: string; id: string }) => ({
        url: createPath("/summarize-website"),
        method: "POST",
        body: { url, id },
      }),
      async onQueryStarted(args, { queryFulfilled, dispatch }) {
        try {
          const {
            data: { link, action },
          } = await queryFulfilled;
          dispatch(
            storyBrainApi.util.updateQueryData(
              "getStoryBrain",
              { id: args.id },
              (draft) => {
                handleCollectionUpdate(draft, link, action, "links");
              },
            ),
          );
        } catch (error) {
          console.error("Failed to summarize website:", error);
        }
      },
    }),
    importStoryBrain: builder.mutation({
      query: ({
        storyBrainId,
        newStoryBrainId,
      }: {
        storyBrainId: string;
        newStoryBrainId: string;
      }) => ({
        url: createPath("/import-story-brain"),
        method: "POST",
        body: { storyBrainId, newStoryBrainId },
      }),
    }),
  }),
});

export const {
  useGetStoryBrainQuery,
  useLazyGetStoryBrainQuery,
  useUpdateStoryBrainMutation,
  useSummarizeUploadedFileMutation,
  useSummarizeWebsiteMutation,
  useImportStoryBrainMutation,
  useInitializeStoryBrainMutation,
} = storyBrainApi;
